import { getUserWorkspaces, type WorkspaceId } from '../config/workspaces';
import { isCreatorStudioPath } from '../config/creatorProgramPaths';
import { IUser, UserRole } from '../types/auth.types';
import { hasCreatorPortalRole } from './creatorPortalAccess';

export type ResolveDefaultWorkspaceInput = {
  user: IUser;
  lastSelectedWorkspace?: WorkspaceId | null;
  creatorOnboardingComplete?: boolean | null;
};

export function isApprovedActiveSeller(user: IUser): boolean {
  return Boolean(user.isActive && user.roles?.includes(UserRole.SELLER));
}

export function isApprovedCreator(user: IUser): boolean {
  return Boolean(user.isActive && hasCreatorPortalRole(user.roles));
}

export function getAccessibleWorkspaceIds(
  user: IUser,
  options?: { creatorOnboardingComplete?: boolean | null },
): Set<WorkspaceId> {
  return new Set(getUserWorkspaces(user, options).map((ws) => ws.id));
}

export function isWorkspaceAccessible(
  user: IUser,
  workspaceId: WorkspaceId | null | undefined,
  options?: { creatorOnboardingComplete?: boolean | null },
): workspaceId is WorkspaceId {
  if (!workspaceId) return false;
  return getAccessibleWorkspaceIds(user, options).has(workspaceId);
}

/** Priority: saved → active seller → approved creator → owner/staff → account. */
export function resolveDefaultWorkspace(input: ResolveDefaultWorkspaceInput): WorkspaceId {
  const { user, lastSelectedWorkspace, creatorOnboardingComplete } = input;
  const accessible = getAccessibleWorkspaceIds(user, { creatorOnboardingComplete });

  if (
    lastSelectedWorkspace &&
    isWorkspaceAccessible(user, lastSelectedWorkspace, { creatorOnboardingComplete })
  ) {
    return lastSelectedWorkspace;
  }

  if (accessible.has('seller') && isApprovedActiveSeller(user)) {
    return 'seller';
  }

  if (accessible.has('creator') && isApprovedCreator(user)) {
    return 'creator';
  }

  if (accessible.has('owner')) return 'owner';
  if (accessible.has('staff')) return 'staff';

  return 'buyer';
}

/** Workspace implied by the current URL, or null on neutral routes (shop, 404, home, etc.). */
export function resolvePathLockedWorkspace(
  user: IUser,
  pathname: string,
  options?: { creatorOnboardingComplete?: boolean | null },
): WorkspaceId | null {
  const accessible = getAccessibleWorkspaceIds(user, options);

  if (pathname.startsWith('/seller') && accessible.has('seller')) {
    return 'seller';
  }
  if (isCreatorStudioPath(pathname) && accessible.has('creator')) {
    return 'creator';
  }
  if (pathname.startsWith('/owner')) {
    if (accessible.has('owner')) return 'owner';
    if (accessible.has('staff')) return 'staff';
  }
  if (pathname.startsWith('/account') && accessible.has('buyer')) {
    return 'buyer';
  }

  return null;
}

export function resolveDisplayedWorkspace(input: {
  user: IUser;
  pathname: string;
  lastSelectedWorkspace?: WorkspaceId | null;
  creatorOnboardingComplete?: boolean | null;
}): WorkspaceId {
  const { user, pathname, lastSelectedWorkspace, creatorOnboardingComplete } = input;
  const pathLocked = resolvePathLockedWorkspace(user, pathname, {
    creatorOnboardingComplete,
  });
  if (pathLocked) return pathLocked;

  return resolveDefaultWorkspace({ user, lastSelectedWorkspace, creatorOnboardingComplete });
}
